import React, { useState, useMemo } from 'react';
import Icon from '../Icon';
import { InputProps } from './types/Input.types';
import clsx from 'clsx';

const Checkbox: React.FC<
  InputProps & React.InputHTMLAttributes<HTMLInputElement>
> = ({
  inputSize = 'medium',
  inputState = 'default',
  wrapperClassName,
  label,
  message,
  helperText,
  showMessageIcon = true,
  ...props
}) => {
  const [checked, setChecked] = useState<boolean>(
    props.checked ?? props.value == 'on'
  );
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    props.onChange && props.onChange(e);
    setChecked(e.target.checked);
  };

  const messageIcon = useMemo(() => {
    switch (inputState) {
      case 'default':
      default:
        return null;

      case 'success':
        return <Icon name="check-circle" />;

      case 'error':
      case 'warning':
        return <Icon name="warning" />;
    }
  }, [inputState]);

  const sizeClass = useMemo(() => {
    switch (inputSize) {
      case 'small':
      case 'xsmall':
        return 'checkbox-wrapper--sm';
      case 'large':
        return 'checkbox-wrapper--lg';
      case 'xlarge':
        return 'checkbox-wrapper--xl';
      case 'medium':
      default:
        return 'checkbox-wrapper--md';
    }
  }, [inputSize]);

  const stateClass = useMemo(() => {
    switch (inputState) {
      case 'error':
        return 'checkbox-wrapper--error';
      case 'warning':
        return 'checkbox-wrapper--warning';
      case 'success':
        return 'checkbox-wrapper--success';
      default:
        return undefined;
    }
  }, [inputState]);

  return (
    <div
      className={clsx(
        wrapperClassName,
        'checkbox-wrapper',
        sizeClass,
        stateClass
      )}
    >
      <label htmlFor={props.id}>
        <div className="wrapper relative">
          <input
            type="checkbox"
            className="checkbox"
            {...props}
            onChange={onChange}
          />
          <Icon
            name="check"
            className={clsx(
              'pointer-events-none absolute left-0 top-0 h-full w-full text-white transition-[opacity] duration-[125ms] ease-linear',
              checked ? 'opacity-100' : 'opacity-0'
            )}
          />
        </div>
        {label && <span>{label}</span>}
      </label>
      {helperText && <span className="helper-text">{helperText}</span>}
      {message && (
        <div className="message">
          {showMessageIcon && messageIcon}
          <span>{message}</span>
        </div>
      )}
    </div>
  );
};

export default Checkbox;
